/**
 * ============================================================================
 * DASHBOARD STRUCTURE REGISTRY — one line per served dashboard.
 * ============================================================================
 *
 * Adding a dashboard = one structure file + one line here (spec
 * multi-dashboard R4.1; same convention as config/views). Lookups fall back
 * to the default (KFE) so an unknown/stale persisted id can never blank the
 * app.
 */

import { kfe } from 'src/config/dashboards/kfe';
import { quality } from 'src/config/dashboards/quality';
import { DashboardStructure } from 'src/config/dashboards/types';
import { WBR_REVIEW_TYPE } from 'src/constants';

// Null-prototype: ids can derive from raw URL segments / persisted state, and
// a normal object would resolve inherited keys ("toString") to functions,
// defeating the `?? kfe` fallback and the `in` check below.
const STRUCTURES: Record<string, DashboardStructure> = Object.assign(
  Object.create(null),
  {
    [kfe.dashboardId]: kfe,
    [quality.dashboardId]: quality,
  },
);

/** The active dashboard's structure; unknown ids fall back to the default (R2.4). */
export function dashboardStructure(dashboardId: string): DashboardStructure {
  return STRUCTURES[dashboardId] ?? kfe;
}

/** A dashboard id the app knows how to render (has a structure file). */
export function isKnownDashboard(dashboardId: string): boolean {
  return dashboardId in STRUCTURES;
}

// ──── URL slug ↔ dashboard id (spec multi-dashboard R5: URL-driven dashboard) ────
// The dashboard rides the URL path as "<urlName>-<review>" (e.g. "kfe-wbr"),
// so links are shareable/bookmarkable and the URL is the single source of
// truth for which dashboard is active. The slug base is the structure's
// `urlName` where it differs from the wire id (KFE's wire id is the legacy
// "fai" — spec dashboard-rename-kfe-quality R1). Review type is WBR for every
// served dashboard today; the slug carries it so multi-review (MBR/QBR) needs
// no URL redesign.

/**
 * slug base → dashboardId, accepting both urlName ("kfe") and wire id ("fai").
 * A Map, not a plain object: the lookup key is a raw URL segment, and a plain
 * object would resolve prototype-inherited keys ("toString", "constructor")
 * to functions instead of missing (AutoSDE finding on CR-304842684).
 */
const ID_BY_SLUG_BASE: Map<string, string> = new Map(
  Object.values(STRUCTURES).flatMap((s) => [
    [s.urlName ?? s.dashboardId, s.dashboardId],
    [s.dashboardId, s.dashboardId],
  ]),
);

/**
 * dashboardId (+ review) → canonical URL slug, e.g. ("fai") → "kfe-wbr".
 * An UNKNOWN id resolves to the DEFAULT dashboard's slug (via the registry's
 * structure fallback) — deliberate: every surface that builds a URL from a
 * stale/foreign id (e.g. a dashboard served by GetPermissions before its UI
 * structure ships) lands on the default, matching the gate's own fallback.
 */
export function toDashboardSlug(
  dashboardId: string,
  reviewType: string = WBR_REVIEW_TYPE,
): string {
  const structure = dashboardStructure(dashboardId);
  return `${structure.urlName ?? structure.dashboardId}-${reviewType.toLowerCase()}`;
}

/**
 * URL slug → dashboardId, or null when the slug names no known dashboard.
 * Strips a trailing "-<review>" ("kfe-wbr" → "kfe"); a bare base also
 * resolves ("kfe"). Legacy bases keep resolving (the wire id "fai" →
 * dashboard "fai"); DashboardGate canonicalizes the URL afterwards (spec
 * dashboard-rename-kfe-quality R1.3). Slug bases carry no dash, so the
 * split is unambiguous.
 */
export function parseDashboardSlug(slug: string | undefined): string | null {
  if (!slug) return null;
  const stripped = slug.replace(/-[^-]+$/, '');
  return ID_BY_SLUG_BASE.get(stripped) ?? ID_BY_SLUG_BASE.get(slug) ?? null;
}

/** Slug of the default (KFE) dashboard — the redirect target for "/" and bad slugs. */
export const DEFAULT_DASHBOARD_SLUG = toDashboardSlug(kfe.dashboardId);

/** Build an in-app path for a dashboard: (id, "/metrics") → "/kfe-wbr/metrics". */
export function dashboardPath(
  dashboardId: string,
  route = '/',
  reviewType: string = WBR_REVIEW_TYPE,
): string {
  const slug = toDashboardSlug(dashboardId, reviewType);
  return route === '/' ? `/${slug}` : `/${slug}${route}`;
}

export type {
  DashboardStructure,
  PageDef,
  ViewConfigRef,
} from 'src/config/dashboards/types';
